import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService, LoginCredentials } from '../services/AuthService';
import { httpClient, LogoutReason } from '../utils/httpClient';
import { User } from 'models/User';
import { catalogoService } from '../services/usuarioService';
import { catalogStorage } from '../utils/catalogStorage';
import { tokenStorage } from '../utils/tokenStorage';
import { prefetchConfigInstitucional } from '../hooks/useConfigInstitucional';

/**
 * Clave de sessionStorage donde se persiste la razón del último logout
 * forzado por el sistema. La LoginPage la lee y la limpia al montarse.
 * Usamos sessionStorage (no localStorage) porque es scoped por pestaña y
 * se pierde al cerrar el navegador — exactamente la semántica que queremos.
 */
const LOGOUT_REASON_KEY = 'auth_logout_reason';

export const persistLogoutReason = (reason: LogoutReason): void => {
  try { sessionStorage.setItem(LOGOUT_REASON_KEY, reason); } catch {}
};

export const consumeLogoutReason = (): LogoutReason | null => {
  try {
    const value = sessionStorage.getItem(LOGOUT_REASON_KEY);
    if (!value) return null;
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return value as LogoutReason;
  } catch {
    return null;
  }
};

// ============================================================
// AuthContext – estado global de autenticación
// ============================================================

interface AuthContextType {
  user:                User | null;
  isAuthenticated:     boolean;
  isLoading:           boolean;
  passwordExpirada:    boolean;
  login:               (credentials: LoginCredentials) => Promise<void>;
  logout:              () => Promise<void>;
  clearPasswordExpirada: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser]                       = useState<User | null>(null);
  const [isLoading, setIsLoading]             = useState(true);
  const [passwordExpirada, setPasswordExpirada] = useState(false);

  // Registrar callback de logout forzado en el httpClient.
  // Permite que cualquier 401 irrecuperable cierre la sesión y que la
  // razón (session-replaced / invalid-token / session-lost) viaje hasta
  // la LoginPage vía sessionStorage para mostrar el banner correcto.
  useEffect(() => {
    httpClient.setLogoutCallback((reason?: LogoutReason) => {
      if (reason) persistLogoutReason(reason);
      catalogStorage.clearAll();
      setUser(null);
    });
  }, []);

  // Precarga los catálogos que no cubre el bootstrap (años escolares y cursos).
  // Grados, secciones y roles ya vienen del bootstrap al arrancar la app.
  // Si el bootstrap falló (offline), carga todo como fallback.
  const precargarCatalogos = () => {
    const fallback = !catalogStorage.isLoaded();
    const siempre = [
      catalogoService.getAñosEscolares().then(d => catalogStorage.saveAñosEscolares(d)),
      catalogoService.getCursos().then(d => catalogStorage.saveCursos(d)),
      // Pre-calentar caché de configuración (flags EVAL_USA_COMPETENCIAS,
      // EVAL_ESCALA_LITERAL, etc.) — el menú y varias pantallas dependen
      // de que estén disponibles antes del primer render.
      prefetchConfigInstitucional(),
    ];
    const extra = fallback ? [
      catalogoService.getGrados().then(d => catalogStorage.saveGrados(d)),
      catalogoService.getSecciones().then(d => catalogStorage.saveSecciones(d)),
      catalogoService.getRoles().then(d => catalogStorage.saveRoles(d)),
    ] : [];
    Promise.all([...siempre, ...extra]).catch(() => {});
  };

  // Restaurar sesión al montar
  useEffect(() => {
    const stored = AuthService.getStoredSession();
    // isSessionValid() comprueba que exista refresh token + usuario guardado
    // El access token puede estar expirado — el httpClient lo renovará automáticamente
    if (stored && AuthService.isSessionValid()) {
      setUser(stored);
      setPasswordExpirada(tokenStorage.isPasswordExpired());
      precargarCatalogos();
    } else {
      AuthService.logout();
    }
    setIsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    const { user: loggedUser, passwordExpirada: expired } = await AuthService.login(credentials);
    setUser(loggedUser);
    setPasswordExpirada(expired);
    if (!expired) precargarCatalogos();
  };

  const logout = async (): Promise<void> => {
    await AuthService.logout();
    catalogStorage.clearAll();
    setPasswordExpirada(false);
    setUser(null);
  };

  const clearPasswordExpirada = () => {
    tokenStorage.savePasswordExpired(false);
    setPasswordExpirada(false);
    precargarCatalogos();
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      passwordExpirada,
      login,
      logout,
      clearPasswordExpirada,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
};